import { addMemberSchema, ROLE_LABELS, ROLES, type MembershipDto } from '@gl/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Trash2, UserPlus, Users } from 'lucide-react';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { EmptyState } from '@/components/EmptyState';
import { FormField } from '@/components/FormField';
import { Spinner } from '@/components/FullScreenLoader';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { api, errorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useCurrentLocal, useHasRole } from '@/lib/local-context';
import { queryKeys } from '@/lib/query';

const ROLE_HELP: Record<string, string> = {
  OWNER: 'Todo, incluido equipo y suscripción',
  MANAGER: 'Menú, precios, caja, estadísticas y apariencia',
  STAFF: 'Caja del día y disponibilidad de productos',
};

export function EquipoPage() {
  const local = useCurrentLocal();
  const isOwner = useHasRole('OWNER');
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [toRemove, setToRemove] = useState<MembershipDto | null>(null);

  const members = useQuery({
    queryKey: queryKeys.members(local.id),
    queryFn: () => api.memberships.list(local.id),
    enabled: isOwner,
  });

  const form = useForm({
    resolver: zodResolver(addMemberSchema),
    defaultValues: { email: '', role: 'STAFF' as const },
  });

  const add = useMutation({
    mutationFn: (body: { email: string; role: (typeof ROLES)[number] }) => api.memberships.add(local.id, body),
    onSuccess: (m) => {
      toast.success(`${m.user.name} se sumó al equipo`);
      form.reset({ email: '', role: 'STAFF' });
      void queryClient.invalidateQueries({ queryKey: queryKeys.members(local.id) });
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const remove = useMutation({
    mutationFn: (m: MembershipDto) => api.memberships.remove(local.id, m.id),
    onSuccess: () => {
      toast.success('Miembro quitado');
      setToRemove(null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.members(local.id) });
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  if (!isOwner) {
    return (
      <Card>
        <CardContent className="text-sm text-muted-foreground">Solo el dueño del local puede ver y editar el equipo.</CardContent>
      </Card>
    );
  }

  const { errors } = form.formState;

  return (
    <div className="grid gap-6">
      <Card>
        <CardHeader>
          <CardTitle>Sumar a alguien</CardTitle>
          <CardDescription>La persona tiene que tener una cuenta creada con ese email.</CardDescription>
        </CardHeader>
        <CardContent>
          <form
            className="grid gap-3 sm:grid-cols-[1fr_180px_auto] sm:items-end"
            onSubmit={form.handleSubmit((v) => add.mutate(v))}
            noValidate
          >
            <FormField label="Email" htmlFor="member-email" error={errors.email?.message}>
              <Input id="member-email" type="email" placeholder="nombre@mail.com" {...form.register('email')} />
            </FormField>
            <FormField label="Rol">
              <Controller
                control={form.control}
                name="role"
                render={({ field }) => (
                  <Select value={field.value} onValueChange={field.onChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLES.map((r) => (
                        <SelectItem key={r} value={r}>
                          {ROLE_LABELS[r]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </FormField>
            <Button type="submit" disabled={add.isPending || local.isDemo}>
              {add.isPending ? <Spinner /> : <UserPlus />} Agregar
            </Button>
          </form>
          {local.isDemo && <p className="mt-2 text-xs text-muted-foreground">En la demo no se puede modificar el equipo.</p>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Equipo</CardTitle>
          <CardDescription>
            {Object.entries(ROLE_HELP).map(([r, help]) => (
              <span key={r} className="block">
                <strong>{ROLE_LABELS[r as keyof typeof ROLE_LABELS]}:</strong> {help}
              </span>
            ))}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {members.isPending ? (
            <div className="flex justify-center py-6">
              <Spinner className="size-5 text-muted-foreground" />
            </div>
          ) : members.error ? (
            <p className="text-sm text-destructive">{errorMessage(members.error)}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Persona</TableHead>
                  <TableHead>Rol</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.data.map((m) => (
                  <TableRow key={m.id}>
                    <TableCell>
                      <p className="font-medium">
                        {m.user.name} {m.userId === user?.id && <span className="text-muted-foreground">(vos)</span>}
                      </p>
                      <p className="text-xs text-muted-foreground">{m.user.email}</p>
                    </TableCell>
                    <TableCell>
                      <Badge variant={m.role === 'OWNER' ? 'default' : 'secondary'}>{ROLE_LABELS[m.role]}</Badge>
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={`Quitar a ${m.user.name}`}
                        disabled={local.isDemo}
                        onClick={() => setToRemove(m)}
                      >
                        <Trash2 />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
          {members.data?.length === 1 && (
            <EmptyState
              compact
              className="mt-2 border-t"
              icon={Users}
              title="Por ahora el equipo sos vos"
              description="Sumá a tu encargado o a quien atiende la caja: cada uno entra con su cuenta y ve solo lo que le toca."
              action={
                !local.isDemo && (
                  <Button
                    size="sm"
                    onClick={() => {
                      const input = document.getElementById('member-email');
                      input?.scrollIntoView({ block: 'center', behavior: 'smooth' });
                      input?.focus({ preventScroll: true });
                    }}
                  >
                    <UserPlus /> Sumar a alguien
                  </Button>
                )
              }
            />
          )}
        </CardContent>
      </Card>

      <Dialog open={!!toRemove} onOpenChange={(open) => !open && setToRemove(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>¿Quitar a {toRemove?.user.name}?</DialogTitle>
            <DialogDescription>Va a perder el acceso a {local.name}. Lo podés volver a sumar cuando quieras.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">Cancelar</Button>
            </DialogClose>
            <Button variant="destructive" disabled={remove.isPending} onClick={() => toRemove && remove.mutate(toRemove)}>
              {remove.isPending && <Spinner />} Quitar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
